import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PATH_INFO, type LearningPath } from '@ready/shared';
import {
  ArrowRight,
  Award,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  Compass,
  Fingerprint,
  Inbox,
  KeyRound,
  Megaphone,
  PlayCircle,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { useApi, type CatalogItem, type MyOrg } from '@/lib/api';
import { CatalogCard, SearchBox } from '@/components/catalog';
import { FindMore, PATH_ICONS, PathCards } from '@/components/paths';
import { PlacementPrompt } from './paths';
import { useMe } from '@/lib/me';
import { useCanUsePasskeys, usePasskeys } from '@/lib/passkeys';
import { isManager } from '@/lib/org-roles';
import { ORG_TYPE_LABELS, ROLE_LABELS } from '@/lib/labels';
import { Badge, Card, ErrorText, GuidanceNote, PageHeader } from '@/components/ui';

/**
 * Dashboard. Learners see what to learn next and their organizations (and
 * how to join one); org admins also see seat usage per organization;
 * platform admins see what needs review.
 * Creating or requesting an organization is rare, so it's a link, not a form.
 */
export function HomePage() {
  const api = useApi();
  const me = useMe();
  const orgs = useQuery({ queryKey: ['myOrgs'], queryFn: api.myOrgs });
  const overview = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: api.admin.overview,
    enabled: Boolean(me.data?.isPlatformAdmin),
  });

  const creator = me.data?.creator;
  const canCreate = Boolean(creator && (creator.unlimited || creator.used < creator.maxOrgs));

  return (
    <div className="space-y-8">
      <PageHeader title="Welcome back" description={me.data?.email} />

      <AccountReminders />

      <PathCallout />

      {overview.data && overview.data.pendingRequests > 0 && (
        <Link to="/admin/requests" className="block">
          <Card className="flex items-center justify-between gap-4 border-primary hover:bg-muted">
            <span className="flex items-center gap-3">
              <Inbox className="size-5 text-primary" aria-hidden />
              <span>
                <span className="font-medium">
                  {overview.data.pendingRequests} organization request
                  {overview.data.pendingRequests === 1 ? '' : 's'}
                </span>{' '}
                waiting for review
              </span>
            </span>
            <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
          </Card>
        </Link>
      )}

      <LearningSection />

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Your organizations</h2>
          <Link to="/join" className="-my-2 flex items-center gap-1.5 py-2 text-sm text-primary">
            <KeyRound className="size-4" aria-hidden />
            Have an invite code?
          </Link>
        </div>
        {orgs.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : orgs.isError ? (
          <ErrorText error={orgs.error} />
        ) : orgs.data.orgs.length === 0 ? (
          <Card>
            <p className="font-medium">You're not in an organization yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Your university or company can add you with an invite code or link. If they've
              verified their email domain, signing in with your work or university email adds you
              automatically.
            </p>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {orgs.data.orgs.map((o) => (
              <OrgCard key={o.id} org={o} />
            ))}
          </div>
        )}
      </section>

      {!me.isPending && (
        <GuidanceNote>
          {canCreate ? (
            <>
              Setting up another organization?{' '}
              <Link to="/organizations/new" className="text-primary">
                Create an organization
              </Link>
              {creator && !creator.unlimited && ` (${creator.used} of ${creator.maxOrgs} used)`}.
            </>
          ) : (
            <>
              Bringing Nirvik Ready to your university or company?{' '}
              <Link to="/organizations/new" className="text-primary">
                Request an organization account
              </Link>
              .
            </>
          )}
        </GuidanceNote>
      )}
    </div>
  );
}

function OrgCard({ org }: { org: MyOrg }) {
  const api = useApi();
  const manager = isManager(org.role);
  const usage = useQuery({
    queryKey: ['usage', org.id],
    queryFn: () => api.usage(org.id),
    enabled: manager,
  });

  return (
    <Link to={`/organizations/${org.id}`}>
      <Card className="h-full hover:border-primary">
        <div className="flex items-start justify-between gap-3">
          <p className="font-medium">{org.name}</p>
          <Badge>{ROLE_LABELS[org.role]}</Badge>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{ORG_TYPE_LABELS[org.type]}</p>
        {manager && usage.data && (
          <p className="mt-3 text-sm tabular-nums text-muted-foreground">
            {usage.data.usage.maxMembers.toLocaleString()} of{' '}
            {usage.data.limits.maxMembers.toLocaleString()} members · {usage.data.planName}
          </p>
        )}
      </Card>
    </Link>
  );
}

/**
 * Home's learning: a search box into Explore, then what needs attention
 * (assigned courses), what's in progress, something new and recent
 * completions, a few of each. Explore has everything else.
 */
function LearningSection() {
  const api = useApi();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const home = useQuery({ queryKey: ['home'], queryFn: api.home });
  const h = home.data;
  const nothing =
    h &&
    !h.assigned.length &&
    !h.upcoming.total &&
    !h.inProgress.total &&
    !h.completed.total &&
    !h.new.total;
  return (
    <section className="space-y-4" aria-labelledby="learning-heading">
      <div className="flex items-center justify-between">
        <h2 id="learning-heading" className="text-lg font-semibold">
          Learning
        </h2>
        <Link
          to="/certificates"
          className="-my-2 flex items-center gap-1.5 py-2 text-sm text-primary"
        >
          <Award className="size-4" aria-hidden /> Your certificates
        </Link>
      </div>
      <SearchBox
        value={text}
        onChange={setText}
        onSubmit={() =>
          navigate(`/learn${text.trim() ? `?q=${encodeURIComponent(text.trim())}` : ''}`)
        }
      />
      {home.isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
      <ErrorText error={home.error} />
      {nothing && (
        <Card>
          <p className="text-sm text-muted-foreground">
            Courses, self-assessments and contests from Nirvik Ready and your organizations will
            appear here.
          </p>
        </Card>
      )}
      {h && h.assigned.length > 0 && (
        <LearningGroup
          icon={<CalendarClock className="size-4" aria-hidden />}
          title="Assigned to you"
          description="From your organizations, soonest due first."
          items={h.assigned}
        />
      )}
      {h && h.upcoming.total > 0 && (
        <LearningGroup
          icon={<CalendarDays className="size-4" aria-hidden />}
          title="Your upcoming events"
          description="Workshops and webinars you've registered for, soonest first."
          items={h.upcoming.items}
          total={h.upcoming.total}
          seeAll="/learn?status=in_progress&sort=soonest"
        />
      )}
      {h && h.openEvents.total > 0 && (
        <LearningGroup
          icon={<Megaphone className="size-4" aria-hidden />}
          title="Workshops and webinars"
          description="Coming up and open for registration. Learn live and ask your questions."
          items={h.openEvents.items}
          total={h.openEvents.total}
          seeAll="/learn?status=not_started&sort=soonest"
        />
      )}
      {h && h.inProgress.total > 0 && (
        <LearningGroup
          icon={<PlayCircle className="size-4" aria-hidden />}
          title="Continue learning"
          items={h.inProgress.items}
          total={h.inProgress.total}
          seeAll="/learn?status=in_progress"
        />
      )}
      {h && h.new.total > 0 && (
        <LearningGroup
          icon={<Sparkles className="size-4" aria-hidden />}
          title="New for you"
          description="Recently published courses and assessments you haven't started."
          items={h.new.items}
          total={h.new.total}
          seeAll="/learn?status=not_started&sort=newest"
        />
      )}
      {h && h.completed.total > 0 && (
        <LearningGroup
          icon={<CheckCircle2 className="size-4" aria-hidden />}
          title="Completed"
          items={h.completed.items}
          total={h.completed.total}
          seeAll="/learn?status=completed"
        />
      )}
      <Link
        to="/learn"
        className="-my-2 inline-flex items-center gap-1.5 py-2 text-sm font-medium text-primary"
      >
        <Compass className="size-4" aria-hidden /> Explore courses, assessments and events
        <ArrowRight className="size-4" aria-hidden />
      </Link>
    </section>
  );
}

function LearningGroup({
  icon,
  title,
  description,
  items,
  total,
  seeAll,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  items: CatalogItem[];
  /** When more exist than shown. */
  total?: number;
  seeAll?: string;
}) {
  const id = `learning-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  const more = total !== undefined && seeAll && total > items.length;
  return (
    <section aria-labelledby={id} className="space-y-3 pt-2">
      <div className="flex items-end justify-between gap-3 border-b border-border pb-2">
        <div>
          <h3 id={id} className="flex items-center gap-2 font-medium">
            <span className="text-primary">{icon}</span>
            {title}
          </h3>
          {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
        </div>
        {more && (
          <Link to={seeAll} className="-my-2 shrink-0 py-2 text-sm text-primary">
            See all {total}
          </Link>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((i) => (
          <CatalogCard key={`${i.kind}-${i.id}`} item={i} />
        ))}
      </div>
    </section>
  );
}

/**
 * Nudges for account setup: a name for certificates until one is on file,
 * and a passkey until the learner has one (where this device supports them).
 */
function AccountReminders() {
  const me = useMe();
  const canUsePasskeys = useCanUsePasskeys();
  const passkeys = usePasskeys();
  const needsName = Boolean(me.data && !me.data.displayName);
  const needsPasskey = canUsePasskeys && passkeys.isSuccess && passkeys.data.length === 0;
  if (!needsName && !needsPasskey) return null;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {needsName && (
        <Reminder
          icon={<UserRound className="size-5 text-primary" aria-hidden />}
          title="Add your name for certificates"
          text="Certificates are issued to the name on your account. Add it now so you're ready when you pass."
          to="/account"
          action="Add your name"
        />
      )}
      {needsPasskey && (
        <Reminder
          icon={<Fingerprint className="size-5 text-primary" aria-hidden />}
          title="Sign in faster with a passkey"
          text="Use your fingerprint, face or device PIN instead of waiting for an email code. Passkeys are more secure, too: they can't be phished."
          to="/account/security"
          action="Set up a passkey"
        />
      )}
    </div>
  );
}

function Reminder({
  icon,
  title,
  text,
  to,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  to: string;
  action: string;
}) {
  return (
    <Card className="flex flex-col justify-between gap-3 border-primary/60">
      <div className="flex items-start gap-3">
        {icon}
        <div>
          <p className="font-medium">{title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{text}</p>
        </div>
      </div>
      <Link
        to={to}
        className="-my-2 inline-flex items-center gap-1.5 self-start py-2 text-sm font-medium text-primary"
      >
        {action} <ArrowRight className="size-4" aria-hidden />
      </Link>
    </Card>
  );
}

/**
 * Where to start: until the learner picks a learning path, the three paths
 * to choose from; then their path's next step and progress.
 */
function PathCallout() {
  const api = useApi();
  const me = useMe();
  const queryClient = useQueryClient();
  const [changing, setChanging] = useState(false);
  const path = me.data?.learningPath ?? null;
  const mine = useQuery({
    queryKey: ['myPath', path],
    queryFn: () => api.myPath(path ?? undefined),
    enabled: Boolean(path),
  });
  const choose = useMutation({
    mutationFn: (p: LearningPath) => api.setPath(p),
    onSuccess: () => {
      setChanging(false);
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      void queryClient.invalidateQueries({ queryKey: ['myPath'] });
    },
  });
  if (me.isPending) return null;

  if (!path || changing)
    return (
      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Where to start</h2>
          <p className="text-sm text-muted-foreground">
            Pick the path that sounds most like you. It sets your next step here; you can switch any
            time.
          </p>
        </div>
        <PathCards current={path} onChoose={(p) => choose.mutate(p)} choosing={choose.isPending} />
        <ErrorText error={choose.error} />
        <PlacementPrompt />
        {changing && (
          <button
            type="button"
            className="-my-2 py-2 text-sm text-muted-foreground underline underline-offset-2"
            onClick={() => setChanging(false)}
          >
            Keep {PATH_INFO[path!].name}
          </button>
        )}
      </section>
    );

  const info = PATH_INFO[path];
  const Icon = PATH_ICONS[path];
  const items = mine.data?.items ?? [];
  const done = items.filter((i) => i.status === 'completed').length;
  const next = items.find((i) => i.slug === mine.data?.next);
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Icon className="size-5 text-primary" aria-hidden />
          Your path: {info.name}
        </h2>
        <div className="flex items-center gap-4 text-sm">
          <Link to={`/paths/${path}`} className="-my-2 py-2 text-primary">
            See the whole path
          </Link>
          <button
            type="button"
            className="-my-2 py-2 text-muted-foreground underline underline-offset-2"
            onClick={() => setChanging(true)}
          >
            Change path
          </button>
        </div>
      </div>
      {mine.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : mine.isError ? (
        <ErrorText error={mine.error} />
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {done} of {items.length} steps done.{' '}
            {next ? 'Up next:' : 'You’ve finished every step on this path.'}
          </p>
          {next ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <CatalogCard item={next} />
            </div>
          ) : (
            <FindMore path={path} />
          )}
        </>
      )}
    </section>
  );
}
